import _get from 'lodash/get';
import BasePlayer from '../base-player';
import HTML5MediaMessage from '../../comm/message/html5-media-message';
import PlayerEvents from '../../events/events';

/**
 *
 * @module HTML5Player
 * @desc Concrete HTML5 video player implementation
 * @class HTML5Player
 *
 */
class HTML5Player extends BasePlayer {

    /**
     *
     * @constructor
     * @param {MediaPlayerConfig} mediaPlayerConfig The MediaPlayerConfig object. Contains the PlayerTypeConfig object used to construct the `<video/>` node.
     *
     */
    constructor(mediaPlayerConfig, observationCenter) {

        super(mediaPlayerConfig, observationCenter);

        /**
         *
         * @type {Rxjs.BehaviorSubject}
         *
         */
        this.playerSubject = this.observationCenter.playerSubject;

        /**
         *
         * @type {Rxjs.BehaviorSubject}
         *
         */
        this.playerTimeSubject = this.observationCenter.playerTimeSubject;


        /**
         *
         * @type {Rxjs.BehaviorSubject}
         *
         */
        this.playerSubject = this.observationCenter.playerSubject;

        /**
         *
         * @type {array}
         *
         */
        this.currentVttItems = undefined;

        /**
         *
         * @type {object}
         * @desc The collection of bound listeners so we can add and remove easily
         *
         */
        this.boundListeners = {};

        this.bindListeners();

    }

    /**
     *
     * @desc Creates a map of bound event listeners for eainsy addition and removal
     *
     */
    bindListeners() {

        this.boundListeners[PlayerEvents.HTML5.MEDIA_LOAD_START] = this.onMediaLoadStart.bind(this);
        this.boundListeners[PlayerEvents.HTML5.CANPLAY] = this.onMediaCanPlay.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_START] = this.onMediaStart.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_LOADED_DATA] = this.onLoadedData.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_LOADED_META] = this.onLoadedMetadata.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_PAUSE] = this.onMediaPause.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_PLAYING] = this.onMediaPlaying.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_SEEKING] = this.onMediaSeeking.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_SEEKED] = this.onMediaSeeked.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_STALLED] = this.onMediaStalled.bind(this);
        this.boundListeners[PlayerEvents.HTML5.MEDIA_COMPLETE] = this.onMediaComplete.bind(this);
        this.boundListeners[PlayerEvents.HTML5.TIME_UPDATE] = this.onTimeUpdate.bind(this);
        this.boundListeners[PlayerEvents.HTML5.DURATION_CHANGE] = this.onDurationChange.bind(this);
        this.boundListeners[PlayerEvents.HTML5.VOLUME_CHANGE] = this.onVolumeChange.bind(this);

    }

    /**
     *
     * @desc creates the `<video/>` tag based on the provided PlayerTypeConfig configuration object.
     *
     */
    createPlayerElement() {

        const videoEl = document.createElement('video');

        videoEl.controls = this.playerTypeConfig.controls;
        videoEl.autoplay = this.playerTypeConfig.autoPlay;
        videoEl.loop = this.playerTypeConfig.loop;
        videoEl.muted = this.playerTypeConfig.mute;

        if (!isNaN(this.playerTypeConfig.width)) {

            videoEl.setAttribute('width', this.playerTypeConfig.width);

        }

        if (!isNaN(this.playerTypeConfig.height)) {

            videoEl.setAttribute('height', this.playerTypeConfig.height);

        }

        if (this.playerTypeConfig.playsInline) {

            videoEl.setAttribute('playsinline', this.playerTypeConfig.playsInline);

        }

        if (this.playerTypeConfig.crossOrigin) {

            videoEl.setAttribute('crossorigin', this.playerTypeConfig.crossOrigin);

        }

        if (this.playerTypeConfig.preload) {

            videoEl.setAttribute('preload', this.playerTypeConfig.preload);

        }

        this.videoEl = videoEl;

        this.initListeners();

    }


    /**
     *
     * @desc Initialize event listeners
     *
     */

    initListeners() {

        super.initListeners();

        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_LOAD_START, this.boundListeners[PlayerEvents.HTML5.MEDIA_LOAD_START]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.CANPLAY, this.boundListeners[PlayerEvents.HTML5.CANPLAY]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_START, this.boundListeners[PlayerEvents.HTML5.MEDIA_START]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_LOADED_DATA, this.boundListeners[PlayerEvents.HTML5.MEDIA_LOADED_DATA]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_LOADED_META, this.boundListeners[PlayerEvents.HTML5.MEDIA_LOADED_META]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_PAUSE, this.boundListeners[PlayerEvents.HTML5.MEDIA_PAUSE]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_PLAYING, this.boundListeners[PlayerEvents.HTML5.MEDIA_PLAYING]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_SEEKING, this.boundListeners[PlayerEvents.HTML5.MEDIA_SEEKING]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_SEEKED, this.boundListeners[PlayerEvents.HTML5.MEDIA_SEEKED]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_STALLED, this.boundListeners[PlayerEvents.HTML5.MEDIA_STALLED]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.MEDIA_COMPLETE, this.boundListeners[PlayerEvents.HTML5.MEDIA_COMPLETE]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.TIME_UPDATE, this.boundListeners[PlayerEvents.HTML5.TIME_UPDATE]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.DURATION_CHANGE, this.boundListeners[PlayerEvents.HTML5.DURATION_CHANGE]);
        this.videoEl.addEventListener(PlayerEvents.HTML5.VOLUME_CHANGE, this.boundListeners[PlayerEvents.HTML5.VOLUME_CHANGE]);

    }

    removeListeners() {

        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_LOAD_START, this.boundListeners[PlayerEvents.HTML5.MEDIA_LOAD_START]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.CANPLAY, this.boundListeners[PlayerEvents.HTML5.CANPLAY]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_START, this.boundListeners[PlayerEvents.HTML5.MEDIA_START]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_LOADED_DATA, this.boundListeners[PlayerEvents.HTML5.MEDIA_LOADED_DATA]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_LOADED_META, this.boundListeners[PlayerEvents.HTML5.MEDIA_LOADED_META]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_PAUSE, this.boundListeners[PlayerEvents.HTML5.MEDIA_PAUSE]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_PLAYING, this.boundListeners[PlayerEvents.HTML5.MEDIA_PLAYING]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_SEEKING, this.boundListeners[PlayerEvents.HTML5.MEDIA_SEEKING]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_SEEKED, this.boundListeners[PlayerEvents.HTML5.MEDIA_SEEKED]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_STALLED, this.boundListeners[PlayerEvents.HTML5.MEDIA_STALLED]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.MEDIA_COMPLETE, this.boundListeners[PlayerEvents.HTML5.MEDIA_COMPLETE]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.TIME_UPDATE, this.boundListeners[PlayerEvents.HTML5.TIME_UPDATE]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.DURATION_CHANGE, this.boundListeners[PlayerEvents.HTML5.DURATION_CHANGE]);
        this.videoEl.removeEventListener(PlayerEvents.HTML5.VOLUME_CHANGE, this.boundListeners[PlayerEvents.HTML5.VOLUME_CHANGE]);

    }


    /**
     *
     * @desc appends the `<video/>` tag to the parent element specified by `domTarget`
     *
     */
    attachPlayerElement() {

        this.parentEl.insertBefore(this.videoEl, this.parentEl.firstChild);

    }

    /**
     *
     * @desc create track nodes as children of the video node
     * @param {array} vttItems List of VttItems
     *
     */
    createTrackNodes(vttItems) {

        const textTracksPromises = [];

        // Only crate new trackNodes
        if(this.currentVttItems === vttItems) {

            return;

        }

        this.currentVttItems = vttItems;

        vttItems.forEach(vttItem => {

            textTracksPromises.push(new Promise((resolve) => {

                vttItem.id = `${vttItem.label} captions`;

                const trackEl = document.createElement('track');

                trackEl.onload = resolve.bind(this, vttItem);
                trackEl.setAttribute('label', vttItem.label);
                trackEl.setAttribute('kind', vttItem.kind);
                trackEl.setAttribute('language', vttItem.srclang);
                trackEl.setAttribute('src', vttItem.src);
                trackEl.setAttribute('id', vttItem.id);

                trackEl.default = vttItem.default;

                this.videoEl.appendChild(trackEl);

            }));

        });

        Promise.all(textTracksPromises).then((savedTracks) => {

            const textTracks = _get(this.videoEl, 'textTracks', []);
            let textTracksAvailable = false;
            if (textTracks.length) {

                const textTracksArr = [].slice.call(textTracks);
                textTracksArr.forEach((textTrack) => {

                    if(textTrack.mode !== 'disabled') {

                        const trackIdx = savedTracks.findIndex((savedTrack) => _get(savedTrack, 'id') === _get(textTrack, 'id'));
                        textTrack.isVttFile = trackIdx !== -1;
                        textTrack.mode = 'hidden';
                        textTracksAvailable = true;

                    }

                });

                if (textTracksAvailable) {

                    this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.TEXT_TRACKS_AVAILABLE, null, textTracks));

                }

            }

        });

    }

    /**
     *
     * @desc Activate given text track (subtitles/captions)
     * @param {int} index (default: 0) Index of the track, in the order given by the textTracks property
     *
     */
    showClosedCaptions(index=0) {

        const textTrack = _get(this.videoEl, `textTracks.${index}`);
        if(textTrack) {

            textTrack.mode = 'showing';
            this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.TEXT_TRACK_ENABLED, null, textTrack));

        }

    }

    /**
     *
     * @desc Hide all text tracks (subtitles/captions)
     *
     */
    hideClosedCaptions() {

        const textTracks = _get(this.videoEl, 'textTracks', []);
        Array.from(textTracks).forEach((track) => {

            track.mode = 'hidden';

        });
        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.TEXT_TRACK_DISABLED, null, textTracks));

    }

    /**
     *
     * @param {string} src The playback URL
     * @abstract
     *
     */
    canPlay(src) {

        const regexp = new RegExp('(\.mp4|\.ogg|\.webm|\.mp3)', 'i');

        return regexp.test(src);

    }

    /**
     *
     * @desc Sets the `src` attribute of the current `<video/>` node
     * @param {MediaItem} mediaItem the MediaItem object to set for playback
     *
     */
    open(mediaItem) {

        if(mediaItem.src) {

            this.currentMediaItem = mediaItem;
            this.videoEl.setAttribute('src', this.currentMediaItem.src);
            this.setPoster(this.currentMediaItem.poster);

        } else {

            throw new Error('MediaItem must have a "src" property.');

        }

    }

    /**
     *
     * @desc Play media when paused or stopped
     *
     */
    play() {

        this.videoEl.play();

    }

    /**
     *
     * @desc Pause media playback
     *
     */
    pause() {

        this.videoEl.pause();

    }

    /**
     *
     * @desc Sets the currentTime to 0.
     *
     */
    replay() {

        this.videoEl.currentTime = 0;
        this.videoEl.play();

    }

    /**
     *
     * @description Set poster image for the HTML5 player
     * @param {string} imgUrl
     * @memberof HTML5Player
     *
     */
    setPoster(imgUrl) {

        if(imgUrl && imgUrl !== '') {

            this.videoEl.setAttribute('poster', imgUrl);

        }

    }

    /**
     *
     * @desc Seek to the specified time
     * @param {Number} time The time to seek to
     *
     */
    seek(time) {

        this.videoEl.currentTime = time;

    }

    destroy() {

        let parentNode;

        if (this.videoEl) {

            this.removeListeners();
            parentNode = _get(this.videoEl, 'parentNode');
            parentNode.removeChild(this.videoEl);

        }

        super.destroy();

    }

    onMediaLoadStart(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_LOAD_START, event));

    }

    onMediaCanPlay(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.CANPLAY, event));

    }

    onMediaStart(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_START, event));

    }

    onLoadedData(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_LOADED_DATA, event));

    }

    onLoadedMetadata(event, data) {

        this.createTrackNodes(this.currentMediaItem.vttItems);
        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_LOADED_META, event));

    }

    onMediaPause(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_PAUSE, event));

    }

    onMediaPlaying(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_PLAYING, event));

    }

    onMediaSeeking(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_SEEKING, event));

    }

    onMediaSeeked(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_SEEKED, event));

    }

    onMediaStalled(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_STALLED, event));

    }

    onMediaComplete(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.MEDIA_COMPLETE, event));

    }

    onTimeUpdate(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.TIME_UPDATE, event));

    }

    onDurationChange(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.DURATION_CHANGE, event));

    }

    onVolumeChange(event, data) {

        this.playerSubject.next(new HTML5MediaMessage(PlayerEvents.HTML5.VOLUME_CHANGE, event));

    }

    removeDefaultControls() {

        this.videoEl.controls = '';

    }

    /**
     *
     * @desc Returns the object type as a string
     * @abstract
     *
     */
    toString() {

        return 'HTML5Player';

    }

}

export default HTML5Player;
